import React, { useState, useRef, useEffect } from 'react';
import { Upload, Youtube } from 'lucide-react';
import { NavigationMenu } from '../../components/NavigationMenu';
import { Button } from '../../components/Button';
import { LoadingAnimation } from '../../components/LoadingAnimation';
import { useCustomNavigate } from '../../hooks/useCustomNavigate';
import { summaryApi } from '../../services/api';
import { useGeneration } from '../../context/GenerationContext';
import { useAuth } from '../../context/AuthContext';
import { PageHeader } from '../../components/PageHeader';
import { useGenerationStatus } from '../../hooks/useGenerationStatus';
import { GenerationLimitsStatus } from '../../components/GenerationLimitsStatus';

type SourceType = 'text' | 'file' | 'youtube';

interface FormData {
  title: string;
  sourceType: SourceType;
  text: string;
  file: File | null;
  link: string;
}

interface ValidationErrors {
  title?: string;
  text?: string;
  file?: string;
  link?: string;
}

function NotesArticle() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    title: '',
    sourceType: 'text',
    text: '',
    file: null,
    link: ''
  });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const navigate = useCustomNavigate();
  const { startGeneration } = useGeneration();
  const { hasActiveSubscription } = useAuth();
  const [formKey, setFormKey] = useState(0);
  const { status: generationStatus, loading: isLimitsLoading, error: limitsError, refetch: refetchGenerationStatus } = useGenerationStatus('Summary');

  // Принудительная очистка формы при монтировании
  useEffect(() => {
    setFormData({
      title: '',
      sourceType: 'text',
      text: '',
      file: null,
      link: ''
    });
    setErrors({});
    setFormKey(Date.now()); // Обновляем key формы для принудительного пересоздания
  }, []);

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Название обязательно';
    }

    switch (formData.sourceType) {
      case 'text':
        if (!formData.text.trim()) {
          newErrors.text = 'Текст обязателен';
        }
        break;
      case 'file':
        if (!formData.file) {
          newErrors.file = 'Файл обязателен';
        }
        break;
      case 'youtube':
        if (!formData.link.trim()) {
          newErrors.link = 'Ссылка обязательна';
        } else if (!isValidYoutubeUrl(formData.link)) {
          newErrors.link = 'Некорректная ссылка на YouTube';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidYoutubeUrl = (url: string): boolean => {
    return url.includes('youtube.com/watch?v=') || url.includes('youtu.be/');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const supportedTypes = [
        'text/plain',
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      
      if (!supportedTypes.includes(file.type)) {
        setErrors({ ...errors, file: 'Поддерживаются только TXT, PDF и DOCX файлы' });
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setErrors({ ...errors, file: 'Размер файла не должен превышать 10MB' });
        return;
      }
      setFormData({ ...formData, file });
      setErrors({ ...errors, file: undefined });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (generationStatus && !generationStatus.canGenerate) {
      return;
    }

    // Проверяем наличие активной подписки
    if (!hasActiveSubscription) {
      navigate('/tariffs');
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Отправляем запрос на сервер (не ждем ответа)
      let responsePromise;
      switch (formData.sourceType) {
        case 'text':
          responsePromise = summaryApi.createFromText({ title: formData.title, text: formData.text });
          break;
        case 'file':
          if (!formData.file) throw new Error('Файл не выбран');
          responsePromise = summaryApi.createFromFile({ title: formData.title, file: formData.file });
          break;
        case 'youtube':
          responsePromise = summaryApi.createFromYouTube({ title: formData.title, youtubeUrl: formData.link });
          break;
        default:
          throw new Error('Неизвестный тип источника');
      }

      // Обрабатываем ответ в фоне
      responsePromise.then((response) => {
        console.log('Задача на создание конспекта запущена:', response);
        
        const taskId = response.data?.taskId;
        if (taskId) {
          // Задача будет автоматически добавлена через SignalR уведомления от бэкенда
          // startGeneration убран, чтобы избежать дублирования
          console.log('TaskId получен:', taskId);
        }
      }).catch((error) => {
        console.error('Error creating notes:', error);
        // Ошибки обрабатываются в фоне, не влияют на UI
      });
      
      // Показываем анимацию 4 секунды, затем переходим на страницу задач
      setTimeout(() => {
        setIsLoading(false);
        navigate('/tasks');
      }, 4000);
      
    } catch (error) {
      console.error('Error starting notes creation:', error);
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800">
        <div className="hidden md:block">
          <NavigationMenu />
        </div>
        <div className="flex flex-col items-center justify-center h-[calc(100vh-96px)]">
          <div className="scale-125 transform">
            <LoadingAnimation size="large" text="Создание конспекта..." />
          </div>
          <div className="mt-16 max-w-2xl text-center">
            <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300">
              Создание конспекта может занять некоторое время.
              <br /><br />
              Ваша задача обрабатывается в фоновом режиме. Вы можете перейти на другую страницу, а результаты будут доступны в разделе "Задачи".
            </p>
          </div>
        </div>
      </div>
    );
  }

  const renderSourceInput = () => {
    switch (formData.sourceType) {
      case 'text':
        return (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Текст</label>
            <textarea
              value={formData.text}
              onChange={(e) => {
                setFormData({ ...formData, text: e.target.value });
                if (errors.text) setErrors({ ...errors, text: undefined });
              }}
              className={`w-full p-3 rounded-lg border ${errors.text ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white min-h-[200px]`}
              placeholder="Вставьте текст для конспекта..."
              autoComplete="off"
            />
            {errors.text && <p className="text-sm text-red-500">{errors.text}</p>}
          </div>
        );
      case 'file':
        return (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Файл (TXT, PDF или DOCX, до 10MB)</label>
            <div className="flex items-center space-x-4">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className={`flex items-center px-4 py-2 rounded-lg border ${errors.file ? 'border-red-500 text-red-500' : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'} hover:bg-gray-100 dark:hover:bg-gray-700`}
              >
                <Upload className="w-5 h-5 mr-2" />
                Выбрать файл
              </button>
              {formData.file && <span className="text-sm text-gray-600 dark:text-gray-400">{formData.file.name}</span>}
            </div>
            <input ref={fileInputRef} type="file" accept=".txt,.pdf,.docx" onChange={handleFileChange} className="hidden" />
            {errors.file && <p className="text-sm text-red-500">{errors.file}</p>}
          </div>
        );
      case 'youtube':
        return (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Ссылка на YouTube</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Youtube className="w-5 h-5 text-gray-400" />
              </div>
              <input
                type="url"
                value={formData.link}
                onChange={(e) => {
                  setFormData({ ...formData, link: e.target.value });
                  if (errors.link) setErrors({ ...errors, link: undefined });
                }}
                className={`w-full pl-10 p-3 rounded-lg border ${errors.link ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white`}
                placeholder="https://www.youtube.com/watch?v=..."
                autoComplete="off"
              />
            </div>
            {errors.link && <p className="text-sm text-red-500">{errors.link}</p>}
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="w-4 h-4 text-amber-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-2">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <strong>Ограничение:</strong> Видео должно быть не длиннее 2 часов 15 минут
                  </p>
                </div>
              </div>
          </div>
        );
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-slate-800 dark:to-gray-900 transition-colors duration-200 mobile-content-padding overflow-x-hidden">
      <div className="hidden md:block">
        <NavigationMenu />
      </div>
      <div className="w-full max-w-7xl mx-auto px-4 py-8 lg:py-12">
        <PageHeader
          title="Конспект"
          subtitle="Создайте структурированный конспект из текста, файла или YouTube видео"
        />
        
        <div className="w-full max-w-2xl mx-auto">
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/50 p-8 overflow-hidden">
            <form key={formKey} onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Название</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => {
                    setFormData({ ...formData, title: e.target.value });
                    if (errors.title) setErrors({ ...errors, title: undefined });
                  }}
                  className={`w-full p-3 rounded-lg border ${errors.title ? 'border-red-500' : 'border-gray-200 dark:border-gray-700'} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white`}
                  placeholder="Введите название конспекта..."
                  autoComplete="off"
                />
                {errors.title && <p className="text-sm text-red-500">{errors.title}</p>}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Источник</label>
                <div className="grid grid-cols-3 gap-4">
                  {[{ id: 'text', label: 'Текст' }, { id: 'file', label: 'Файл' }, { id: 'youtube', label: 'YouTube' }].map((source) => (
                    <button
                      key={source.id}
                      type="button"
                      onClick={() => setFormData({ ...formData, sourceType: source.id as SourceType })}
                      className={`p-3 rounded-lg border ${formData.sourceType === source.id ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'} hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors`}
                    >
                      {source.label}
                    </button>
                  ))}
                </div>
              </div>

              {renderSourceInput()}

              <GenerationLimitsStatus 
                className="mb-4" 
                generationType="Summary" 
                status={generationStatus}
                loading={isLimitsLoading}
                error={limitsError}
              />

              <Button type="submit" variant="primary" fullWidth disabled={!generationStatus?.canGenerate}>
                Сгенерировать конспект
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NotesArticle;
